import { useState } from 'react';
import { Building2, HelpCircle, Download, Plus, Trash2 } from 'lucide-react';
import ToolPage from '@/components/ToolPage';

interface Investment {
  id: string;
  description: string;
  purchasePrice: number;
  salePrice: number;
  purchaseDate: string;
  saleDate: string;
  expenses: number;
}

interface TaxYear {
  year: string;
  allowance: number;
  basicRate: number;
  higherRate: number;
}

const taxYears: TaxYear[] = [
  {
    year: '2022/23',
    allowance: 12300,
    basicRate: 10,
    higherRate: 20
  },
  {
    year: '2023/24',
    allowance: 6000,
    basicRate: 10,
    higherRate: 20
  },
  {
    year: '2024/25',
    allowance: 3000,
    basicRate: 10,
    higherRate: 20
  },
  {
    year: '2025/26',
    allowance: 3000,
    basicRate: 10,
    higherRate: 20
  },
  {
    year: '2026/27',
    allowance: 3000,
    basicRate: 10,
    higherRate: 20
  }
];

export default function CapitalGainsCalculator() {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [annualIncome, setAnnualIncome] = useState<string>('');
  const [selectedTaxYear, setSelectedTaxYear] = useState<TaxYear>(taxYears[1]); // Default to 2023/24
  const [newInvestment, setNewInvestment] = useState<Investment>({
    id: '',
    description: '',
    purchasePrice: 0,
    salePrice: 0,
    purchaseDate: '',
    saleDate: '',
    expenses: 0
  });

  const calculateGain = (investment: Investment) => {
    return investment.salePrice - investment.purchasePrice - investment.expenses;
  };

  const calculateTotalGain = () => {
    return investments.reduce((sum, inv) => sum + calculateGain(inv), 0);
  };

  const calculateTaxableGain = () => {
    const totalGain = calculateTotalGain();
    return Math.max(0, totalGain - selectedTaxYear.allowance);
  };

  const calculateTax = () => {
    const taxableGain = calculateTaxableGain();
    const income = Number(annualIncome) || 0;
    
    // Basic rate threshold
    const basicRateThreshold = 50270;
    
    if (income >= basicRateThreshold) {
      // Higher rate taxpayer
      return taxableGain * (selectedTaxYear.higherRate / 100);
    } else {
      const remainingBasicRate = basicRateThreshold - income;
      
      if (taxableGain <= remainingBasicRate) {
        // All gain taxed at basic rate
        return taxableGain * (selectedTaxYear.basicRate / 100);
      } else {
        // Part basic rate, part higher rate
        const basicRateTax = remainingBasicRate * (selectedTaxYear.basicRate / 100);
        const higherRateTax = (taxableGain - remainingBasicRate) * (selectedTaxYear.higherRate / 100);
        return basicRateTax + higherRateTax;
      }
    }
  };

  const handleAddInvestment = () => {
    if (!newInvestment.description || 
        newInvestment.purchasePrice <= 0 || 
        newInvestment.salePrice <= 0 || 
        !newInvestment.purchaseDate || 
        !newInvestment.saleDate) {
      return;
    }

    setInvestments([...investments, { ...newInvestment, id: `inv-${Date.now()}` }]);
    setNewInvestment({
      id: '',
      description: '',
      purchasePrice: 0,
      salePrice: 0,
      purchaseDate: '',
      saleDate: '',
      expenses: 0
    });
    setShowAddModal(false);
  };

  const handleDeleteInvestment = (id: string) => {
    setInvestments(investments.filter(inv => inv.id !== id));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadCalculations = () => {
    let csv = 'Asset,Purchase Price,Sale Price,Expenses,Capital Gain\n';
    
    investments.forEach(inv => {
      csv += `${inv.description},${inv.purchasePrice},${inv.salePrice},${inv.expenses},${calculateGain(inv)}\n`;
    });

    csv += '\nSummary\n';
    csv += `Tax Year,${selectedTaxYear.year}\n`;
    csv += `Total Gains,${calculateTotalGain()}\n`;
    csv += `Tax-Free Allowance,${selectedTaxYear.allowance}\n`;
    csv += `Taxable Gain,${calculateTaxableGain()}\n`;
    csv += `Tax Due,${calculateTax()}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'capital-gains-calculation.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <ToolPage title="Capital Gains Calculator" description="Calculate Capital Gains Tax liability" icon={Building2}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Tax Year</h2>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <HelpCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {taxYears.map((year) => (
                  <button
                    key={year.year}
                    onClick={() => setSelectedTaxYear(year)}
                    className={`p-3 text-sm rounded-lg border-2 transition-all ${
                      selectedTaxYear.year === year.year
                        ? 'border-brand-500 bg-brand-50 text-brand-800'
                        : 'border-gray-200 hover:border-brand-300 text-gray-600'
                    }`}
                  >
                    {year.year}
                  </button>
                ))}
              </div>

              {showInfo && (
                <div className="p-4 bg-brand-50 rounded-lg mb-6">
                  <h3 className="font-medium text-brand-900 mb-2">Tax Year Details</h3>
                  <ul className="text-sm text-brand-800 space-y-1">
                    <li>Tax-free allowance: {formatCurrency(selectedTaxYear.allowance)}</li>
                    <li>Basic rate: {selectedTaxYear.basicRate}%</li>
                    <li>Higher rate: {selectedTaxYear.higherRate}%</li>
                  </ul>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Income (Optional)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                  <input
                    type="number"
                    min="0"
                    value={annualIncome}
                    onChange={(e) => setAnnualIncome(e.target.value)}
                    placeholder="Enter your annual income"
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Used to determine your tax rate
                </p>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="font-medium text-gray-900">Assets Sold</h3>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Asset
                </button>
              </div>

              {showAddModal && (
                <div className="mt-6 space-y-4">
                  <input
                    type="text"
                    value={newInvestment.description}
                    onChange={(e) => setNewInvestment({ ...newInvestment, description: e.target.value })}
                    placeholder="Asset description"
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Purchase Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          value={newInvestment.purchasePrice || ''}
                          onChange={(e) => setNewInvestment({ ...newInvestment, purchasePrice: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sale Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                        <input
                          type="number"
                          min="0"
                          value={newInvestment.salePrice || ''}
                          onChange={(e) => setNewInvestment({ ...newInvestment, salePrice: Number(e.target.value) })}
                          className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Purchase Date
                      </label>
                      <input
                        type="date"
                        value={newInvestment.purchaseDate}
                        onChange={(e) => setNewInvestment({ ...newInvestment, purchaseDate: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sale Date
                      </label>
                      <input
                        type="date"
                        value={newInvestment.saleDate}
                        onChange={(e) => setNewInvestment({ ...newInvestment, saleDate: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expenses (Optional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                      <input
                        type="number"
                        min="0"
                        value={newInvestment.expenses || ''}
                        onChange={(e) => setNewInvestment({ ...newInvestment, expenses: Number(e.target.value) })}
                        placeholder="Selling costs, improvements, etc."
                        className="w-full pl-8 pr-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddInvestment}
                      className="px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-800 transition-colors"
                    >
                      Add Asset
                    </button>
                  </div>
                </div>
              )}

              {/* Assets List */}
              <div className="mt-6 space-y-4">
                {investments.map((investment) => (
                  <div
                    key={investment.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group"
                  >
                    <div>
                      <h3 className="font-medium text-gray-900">{investment.description}</h3>
                      <p className="text-sm text-gray-500">
                        Purchased: {new Date(investment.purchaseDate).toLocaleDateString()}
                        {' • '}
                        Sold: {new Date(investment.saleDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {formatCurrency(calculateGain(investment))}
                        </p>
                        <p className="text-sm text-gray-500">
                          Gain/Loss
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteInvestment(investment.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {investments.length === 0 && !showAddModal && (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No assets added yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Tax Summary</h2>
                <button
                  onClick={downloadCalculations}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                  disabled={investments.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Gains</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calculateTotalGain())}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Tax-Free Allowance</p>
                  <p className="text-2xl font-bold text-brand-700">
                    {formatCurrency(selectedTaxYear.allowance)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Taxable Gain</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(calculateTaxableGain())}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Estimated Tax Due</p>
                  <p className="text-3xl font-bold text-brand-700">
                    {formatCurrency(calculateTax())}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-brand-50 rounded-xl p-6">
              <h3 className="font-semibold text-brand-900 mb-2">Important Notes</h3>
              <ul className="text-sm text-brand-800 space-y-2">
                <li>This is an estimate based on current tax rates and allowances</li>
                <li>Different rates may apply for residential property</li>
                <li>Consider seeking professional advice for complex situations</li>
              </ul>
            </div>
          </div>
        </div>
    </ToolPage>
  );
}